import {Controller} from "stimulus"

// Roughly how long the agent takes, used to pace the progress bar. It is a
// pacing hint, not a deadline -- the bar deliberately stops short of full
// rather than implying the request has given up.
const EXPECTED_SECONDS = 20;
const MAX_PROGRESS = 0.95;

// How long the search term must hold still before we spend a request on it.
// Each request ties up a Rails worker for the length of a 10-20s agent call,
// and aborting the browser fetch does not interrupt that server-side work, so
// the cheapest fix is to not start it for a term the user is still editing.
const SETTLE_MS = 1000;

// Fetches AI NAICS suggestions for a search that has already returned its
// database results. The agent runs a reasoning model and takes 10-20 seconds to
// answer, so these arrive in a second request rather than making the user wait.
//
// The element is rendered as part of the search results, so a new search
// replaces it and disconnect() aborts any request still in flight -- a stale
// response can never overwrite a newer search.
export default class extends Controller {
    static targets = ["pending", "suggestions", "emptyState", "elapsed", "progress"];

    connect() {
        this.abortController = new AbortController();
        // A newer search replaces this element, so disconnect() cancels the
        // timer and the superseded term never reaches the server.
        this.settleTimer = setTimeout(() => this.fetchSuggestions(), this.settleDelay());
    }

    disconnect() {
        clearTimeout(this.settleTimer);
        this.stopElapsedTimer();
        this.abortController.abort();
    }

    settleDelay() {
        const configured = this.data.get("settleDelay");
        return configured === null ? SETTLE_MS : parseInt(configured, 10);
    }

    // The agent is non-deterministic, so a poor or empty answer is often worth
    // simply asking again for the same search term.
    retry(event) {
        event.preventDefault();

        this.abortController.abort();
        this.abortController = new AbortController();
        this.suggestionsTarget.innerHTML = "";
        this.pendingTarget.classList.remove("hidden");
        this.fetchSuggestions();
    }

    fetchSuggestions() {
        const search = this.data.get("search");
        if (!search) {
            this.hidePending();
            return;
        }

        this.startElapsedTimer();

        fetch(this.requestUrl(search), {signal: this.abortController.signal})
            .then(response => {
                // fetch only rejects on network failure, so without this an
                // error or login page would be injected as markup.
                if (!response.ok) {
                    throw new Error(`Recommendations request failed: ${response.status}`);
                }
                return response.text();
            })
            .then(html => this.showSuggestions(html))
            .catch(error => {
                if (error.name !== "AbortError") {
                    console.warn(error);
                    this.showUnavailable();
                }
            });
    }

    requestUrl(search) {
        const params = new URLSearchParams();
        params.append("search", search);

        const classificationType = this.data.get("classificationType");
        if (classificationType) {
            params.append("classification_type", classificationType);
        }

        this.excludedCodes().forEach(code => params.append("exclude_codes[]", code));

        return `${this.data.get("url")}?${params.toString()}`;
    }

    excludedCodes() {
        try {
            return JSON.parse(this.data.get("excludeCodes") || "[]");
        } catch (error) {
            console.warn(error);
            return [];
        }
    }

    showSuggestions(html) {
        this.hidePending();
        this.suggestionsTarget.innerHTML = html;

        // Only retract "no matching classifications found" once real rows have
        // landed -- an outage notice leaves the message correct.
        if (this.hasEmptyStateTarget && this.suggestionsTarget.querySelector(".selectable-result")) {
            this.emptyStateTarget.classList.add("hidden");
        }
    }

    showUnavailable() {
        this.hidePending();

        const message = this.data.get("unavailableMessage");
        if (!message) {
            return;
        }

        const note = document.createElement("div");
        note.className = "flex items-center gap-2 border-t border-gray-200 px-4 py-3 text-sm text-gray-500";
        note.textContent = message;

        this.suggestionsTarget.innerHTML = "";
        this.suggestionsTarget.appendChild(note);
    }

    startElapsedTimer() {
        // A retry starts a second wait; without this the first interval would
        // keep running and the count would climb twice per second.
        this.stopElapsedTimer();
        this.secondsWaiting = 0;
        this.renderElapsed();
        this.elapsedTimer = setInterval(() => {
            this.secondsWaiting += 1;
            this.renderElapsed();
        }, 1000);
    }

    renderElapsed() {
        if (this.hasElapsedTarget) {
            this.elapsedTarget.textContent = `${this.secondsWaiting}s`;
        }

        if (this.hasProgressTarget) {
            const ratio = Math.min(this.secondsWaiting / EXPECTED_SECONDS, MAX_PROGRESS);
            this.progressTarget.style.setProperty("--naics-progress", `${Math.round(ratio * 100)}%`);
        }
    }

    stopElapsedTimer() {
        if (this.elapsedTimer) {
            clearInterval(this.elapsedTimer);
            this.elapsedTimer = null;
        }
    }

    hidePending() {
        this.stopElapsedTimer();
        this.pendingTarget.classList.add("hidden");
    }
}
